import { SalesforceProduct } from "@shared/index";
import { FormProduct, Product, ProductConnection, SkuImportDraftState } from "@shared/types";

// Keys used for writing to local storage
export const productLocalStorageKeys = {
  skuImportDraft: "@skuImportDraft",
};

export type UseProductsUtils = {
  handleInstallationId: (installationId: string) => void;
  handlePostSkuImportDraft: (skuImportDraft: FormProduct) => void;
  handleUpdateSkuImportDrafts: (skuImportDrafts: FormProduct[], status: SkuImportDraftState) => void;
  handlePostProducts: (serviceId: string, accessToken: string, products: Partial<SalesforceProduct>[]) => Promise<void>;
};
export type ProductsContextState = [state: ProductsState, dispatch: ProductsDispatch, utils?: UseProductsUtils];

export type LoadingKey = "remoteProducts" | "localProducts" | "skuImportDrafts" | "serviceProducts" | "connections";

export type LoadingState = {
  [key in LoadingKey]: boolean;
};

export type LoadingKeys = keyof LoadingState;
export type ErrorKey = "remoteProducts" | "localProducts" | "skuImportDrafts" | "serviceProducts" | "connections";
export type ErrorState = {
  [key in ErrorKey]: Error | undefined;
};

export interface ProductsState {
  initialized: boolean;
  loading: LoadingState;
  installationId: string | undefined;
  remoteProducts: Product[] | undefined;
  skuImportDrafts: FormProduct[] | undefined;
  filteredRemoteProducts: Product[] | undefined;
  localProducts: Product[] | undefined;
  filteredLocalProducts: Product[] | undefined;
  serviceProducts: Product[] | undefined;
  connections: ProductConnection[] | undefined;
  filteredConnections: ProductConnection[] | undefined;
  error: ErrorState;
  remoteFormProducts: FormProduct[] | undefined;
  remoteFormProductsWithImported: FormProduct[] | undefined;
  localParentChildProducts: Product[] | undefined;
}

export interface SetSkuImportDraftsAction {
  type: "SET_SKU_IMPORT_DRAFTS";
  payload: {
    skuImportDrafts: FormProduct[];
  };
}

export interface SetRemoteFormProductsWithImportsAction {
  type: "SET_REMOTE_FORM_PRODUCTS_WITH_IMPORTS";
  payload: {
    remoteFormProductsWithImports: FormProduct[];
  };
}
export interface PostSkuImportDraftAction {
  type: "ADD_SKU_IMPORT_DRAFT";
  payload: {
    skuImportDraft: FormProduct;
  };
}

export interface EditSkuImportDraftAction {
  type: "EDIT_SKU_IMPORT_DRAFT";
  payload: {
    skuImportDrafts: FormProduct[];
    status: SkuImportDraftState;
  };
}

export interface SetLocalProductsAction {
  type: "SET_LOCAL_PRODUCTS";
  payload: {
    localProducts: Product[];
  };
}

export interface SetFilteredLocalProductsAction {
  type: "SET_FILTERED_LOCAL_PRODUCTS";
  payload: {
    filteredLocalProducts: Product[];
  };
}
export interface SetRemoteProductsAction {
  type: "SET_REMOTE_PRODUCTS";
  payload: {
    remoteProducts: Product[];
  };
}

export interface SetRemoteFormProductsAction {
  type: "SET_REMOTE_FORM_PRODUCTS";
  payload: {
    remoteFormProducts: FormProduct[];
  };
}

export interface SetRemoteFormProductsWithImportsAction {
  type: "SET_REMOTE_FORM_PRODUCTS_WITH_IMPORTS";
  payload: {
    remoteFormProductsWithImports: FormProduct[];
  };
}

export interface SetFilteredRemoteProductsAction {
  type: "SET_FILTERED_REMOTE_PRODUCTS";
  payload: {
    filteredRemoteProducts: Product[];
  };
}

export interface SetServiceProductsAction {
  type: "SET_SERVICE_PRODUCTS";
  payload: {
    serviceProducts: Product[];
  };
}

export interface SetConnectionsAction {
  type: "SET_CONNECTIONS";
  payload: {
    connections: ProductConnection[];
  };
}

export interface SetFilteredConnectionsAction {
  type: "SET_FILTERED_CONNECTIONS";
  payload: {
    filteredConnections: ProductConnection[];
  };
}

export interface SetLocalParentChildProductsAction {
  type: "SET_LOCAL_PARENT_CHILD_PRODUCTS";
  payload: {
    localParentChildProducts: Product[];
  };
}

export type SetInitializedAction = {
  type: "SET_INITIALIZED";
  payload: {
    initialized: boolean;
  };
};

export type SetErrorAction = {
  type: "SET_ERROR";
  payload: {
    errorKey: ErrorKey;
    error: Error | undefined;
  };
};

export type SetLoadingAction = {
  type: "SET_LOADING";
  payload: {
    loadingKey: LoadingKey;
    loading: boolean;
  };
};

export type ProductsActions =
  | SetConnectionsAction
  | SetErrorAction
  | SetRemoteFormProductsAction
  | SetInitializedAction
  | SetLoadingAction
  | SetLocalProductsAction
  | SetRemoteProductsAction
  | SetServiceProductsAction
  | SetFilteredConnectionsAction
  | SetFilteredLocalProductsAction
  | SetFilteredRemoteProductsAction
  | SetSkuImportDraftsAction
  | PostSkuImportDraftAction
  | EditSkuImportDraftAction
  | SetRemoteFormProductsWithImportsAction
  | SetLocalParentChildProductsAction;

type ProductsDispatch = React.Dispatch<ProductsActions>;
